/**
 * Cart Engine v1.0.0 — `<cart-engine>` web component
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Singleton web component that owns canonical cart state, fetch, last-write-wins
 * concurrency, section morph orchestration, and event dispatch.
 *
 * MENTAL MODEL
 * ─────────────────────────────────────────────────────────────────────────────
 * The engine is the truth. `$store.cart` and `$store.discount` are reads-only
 * Alpine projections that subscribe to `cart:update` and copy state into
 * reactive form. Templates invoke actions via `$cart.X()` (Alpine magic that
 * resolves to `window.cartEngine`).
 *
 *   <cart-engine>            ← truth, fetch, LWW, morph, event dispatch
 *      │
 *      ├─ dispatches cart:update / cart:error / discount:applied / discount:failed
 *      │
 *      ├─ window.cartEngine  ← JS/WC consumers
 *      ├─ $cart magic        ← Alpine templates (`$cart.add(...)`)
 *      └─ $store.cart        ← reactive reads (`$store.cart.item_count`, etc.)
 *
 * SLICE 4/5/7 (#16) collapses the three previously-planned slices into one
 * clean break per the no-backwards-compat directive.
 *
 * ADD MODES
 * ─────────────────────────────────────────────────────────────────────────────
 *   silent → fetch + morph; NO drawer open, NO navigation
 *   open   → fetch + morph; engine signals drawer open via cart:update
 *            (cart-drawer subscribes and opens itself by default; Slice 6 / #18
 *             made auto-open the hardcoded behavior, no opt-in attribute)
 *   page   → navigate to pageUrl || Shopify.routes.cart_url; no section morph
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { Component } from './component-base.js';

const Events = window.Theme?.events;
if (!Events) {
  console.error('[cart-engine] window.Theme.events not found — events.js must load before cart-engine.js');
}

const {
  CartUpdateEvent,
  CartErrorEvent,
  DiscountAppliedEvent,
  DiscountFailedEvent,
} = Events || {};

const formatMoney = (cents) =>
  (window.Theme && typeof window.Theme.formatMoney === 'function')
    ? window.Theme.formatMoney(cents)
    : '';

/**
 * Cart Engine Web Component
 *
 * Lifecycle:
 *   - constructor: namespaced logger (`this.log`)
 *   - connectedCallback: bootstrap state from `window.Theme.cartData`,
 *     instantiate the cart section morph adapter, expose `window.cartEngine`
 *
 * Public API (all actions):
 *   add(opts)               → Promise<cart>
 *   changeItem({ key, quantity }) → Promise<cart>
 *   removeItem(key)         → Promise<cart>
 *   undoRemove()            → Promise<cart>
 *   increment(key)          → Promise<cart>
 *   decrement(key)          → Promise<cart>
 *   applyDiscount(code)     → Promise<boolean>
 *   removeDiscount(code)    → Promise<boolean>
 *   refresh()               → Promise<cart>
 *
 * Public API (reads):
 *   getState()              → snapshot of current state
 *   hasItem(productId)      → boolean
 *   quantityOf(productId)   → number
 *   findItem(productId)     → line item | null
 */
export class CartEngine extends Component {
  // ─── State (canonical truth) ─────────────────────────────────────────────
  // Items: array of line items as returned by /cart.js
  items = [];
  item_count = 0;
  total_price = 0;
  total_discount = 0;
  discount_codes = [];

  // UI state — projected to $store.cart for reactivity
  loading = false;
  itemLoading = {}; // { [key]: true }
  error = null;
  discountLoading = false;
  discountError = null;

  // Undo snapshot (last removed item)
  removedItem = null;

  // Variant ids with an upsell toggle currently in flight. Used by setUpsell()
  // as a per-variant lock so a rapid repeat click can't fire a second write.
  _upsellPending = new Set();

  // The morph adapter — engine owns it
  sectionAdapter = null;

  connectedCallback() {
    super.connectedCallback();

    // SSR bootstrap from theme.liquid → window.Theme.cartData
    const ssrCart = window.Theme?.cartData || {};
    this.items = ssrCart.items || [];
    this.item_count = ssrCart.item_count || 0;
    this.total_price = ssrCart.total_price || 0;
    this.total_discount = ssrCart.total_discount || 0;
    this.discount_codes = ssrCart.discount_codes || [];

    // Engine owns the morph adapter. Created here so the engine's lifetime
    // tracks its own DOM consequences. The IIFE in section-morph-adapter.js
    // no longer instantiates this (Slice 4 ownership move).
    const eventName = Events?.NAMES?.cartUpdate || 'cart:update';
    if (typeof window.Theme?.createMorphAdapter === 'function') {
      this.sectionAdapter = window.Theme.createMorphAdapter({
        eventName,
        sectionAttribute: 'data-cart-section',
      });
    } else {
      this.log.error('createMorphAdapter not available — section-morph-adapter.js missing?');
      this.sectionAdapter = {
        register: () => {},
        unregister: () => {},
        getIds: () => [],
      };
    }

    // Make this instance globally addressable. Tests and non-Alpine consumers
    // reach the engine via `window.cartEngine`. Templates use `$cart` magic
    // (registered in alpine-store-cart.js), which simply returns this same ref.
    window.cartEngine = this;

    // Emit an initial cart:update so projections (Alpine $store.cart) seed
    // their reactive state without depending on the SSR bootstrap path.
    this._dispatchUpdate({ action: 'init' }, null);

    this.log.info('Connected', { item_count: this.item_count });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (window.cartEngine === this) {
      window.cartEngine = null;
    }
  }

  // ─── Reads ────────────────────────────────────────────────────────────────

  getState() {
    return {
      items: this.items,
      item_count: this.item_count,
      total_price: this.total_price,
      total_discount: this.total_discount,
      discount_codes: this.discount_codes,
      loading: this.loading,
      itemLoading: this.itemLoading,
      error: this.error,
      discountLoading: this.discountLoading,
      discountError: this.discountError,
      removedItem: this.removedItem,
    };
  }

  findItem(productId) {
    if (productId == null) return null;
    const idNum = Number(productId);
    return this.items.find((i) => {
      // Items may match by product_id or variant_id; the helper takes either.
      return Number(i.product_id) === idNum || Number(i.variant_id) === idNum;
    }) || null;
  }

  hasItem(productId) {
    return !!this.findItem(productId);
  }

  quantityOf(productId) {
    const item = this.findItem(productId);
    return item ? item.quantity : 0;
  }

  // ─── Internal: dispatch / morph ──────────────────────────────────────────

  _dispatchUpdate(data, sections, sourceId = null) {
    if (!CartUpdateEvent) return;
    const detail = {
      cart: this.getState(),
      // Keep the legacy sourceId/data/sections shape so existing listeners
      // continue to work without rewrites.
      sourceId,
      data: data || {},
      sections: sections || null,
    };
    try {
      document.dispatchEvent(new CartUpdateEvent(detail.cart, sourceId, data, sections));
    } catch (err) {
      this.log.error('Failed to dispatch cart:update', err);
    }
  }

  _dispatchError(err, sourceId = null, body = null) {
    if (!CartErrorEvent) return;
    try {
      document.dispatchEvent(new CartErrorEvent(err, sourceId, body));
    } catch (e) {
      this.log.error('Failed to dispatch cart:error', e);
    }
  }

  _merge(cart) {
    if (!cart) return;
    // Whitelist: only the fields we surface explicitly. Server-only fields
    // (requires_shipping, original_total_price, etc.) are intentionally
    // omitted — any future UI need must wire them in explicitly.
    const whitelist = ['items', 'item_count', 'total_price', 'total_discount', 'discount_codes'];
    whitelist.forEach((key) => {
      if (key in cart) this[key] = cart[key];
    });
  }

  // ─── Internal: fetch helpers ─────────────────────────────────────────────

  async _fetchCart() {
    try {
      const res = await fetch('/cart.js');
      if (!res.ok) {
        this.log.error('Failed to get cart', { status: res.status });
        return null;
      }
      return res.json();
    } catch (err) {
      this.log.error('Failed to get cart', err);
      return null;
    }
  }

  /**
   * POST a cart mutation through the latestOnly('cart', ...) LWW gate.
   *
   * Returns:
   *   - { result, isLatest } on success (or stale)
   *   - null if request was aborted by a newer call
   *
   * On HTTP error, dispatches CartErrorEvent and throws.
   *
   * Critical: handles the empty-error-body case gracefully (the bug Slice 4
   * inherits from the old store). Some Shopify endpoints return 200 with HTML
   * or 4xx with no body; we don't want a SyntaxError to surface as the user's
   * "cart broken" failure mode.
   */
  async _postCart(endpoint, body, sourceId = null) {
    const adapterIds = this.sectionAdapter?.getIds?.() || [];
    if (adapterIds.length > 0) {
      body.sections = adapterIds.join(',');
    }

    const latestOnly = window.Theme?.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.log.error('latestOnly not available — concurrency.js missing?');
      throw new Error('latestOnly not available');
    }

    const outcome = await latestOnly('cart', async (signal) => {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal,
      });

      if (!res.ok) {
        // Graceful empty-body handling — some Shopify endpoints respond
        // 4xx with an empty body. Surface a structured error either way.
        let err = {};
        try {
          err = await res.json();
        } catch {
          err = {
            description: `HTTP ${res.status} ${res.statusText || ''}`.trim(),
            status: res.status,
          };
        }
        this._dispatchError(err, sourceId, body);
        throw err;
      }

      // Success: also gracefully handle empty bodies (e.g. /cart/update.js
      // with no changes can return 200 + empty). Caller will treat as no-op.
      try {
        return await res.json();
      } catch {
        return {};
      }
    });

    if (outcome === null) {
      this.log.debug('Request aborted (LWW)', { endpoint });
      return null;
    }

    const { result, isLatest } = outcome;

    // Dispatch + morph: the morph adapter listens to cart:update and forwards
    // sections payload to Theme.morphSections. We only carry sections on the
    // latest response so stale responses cannot blat newer state.
    const sectionsForEvent = result && result.sections && isLatest ? result.sections : null;
    // Note: data.action is set by the caller via wrapper methods below.
    return { result, isLatest, sectionsForEvent };
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  /**
   * Add an item (or items) to the cart.
   *
   * @param {Object} options
   *   - id:        variant id (or items[])
   *   - quantity:  number (default 1)
   *   - properties: object (line item properties)
   *   - selling_plan: selling plan id
   *   - mode:      'silent' | 'open' | 'page' (default 'open')
   *   - pageUrl:   used when mode === 'page' (default Shopify.routes.cart_url)
   *   - sourceId:  caller-supplied id propagated through event detail
   *   - items:     alternative: array of items
   */
  async add(options = {}) {
    const mode = options.mode || 'open';
    const sourceId = options.sourceId || null;

    // Page mode: navigate, skip everything else. The browser unloads the page,
    // so there's no point in optimistic state or morph.
    if (mode === 'page') {
      const url = options.pageUrl || (window.Shopify?.routes?.cart_url) || '/cart';
      // Still POST the add so the cart has the item when the next page loads.
      const items = options.items || [{
        id: options.id,
        quantity: options.quantity || 1,
        properties: { ...(options.properties || {}) },
        ...(options.selling_plan && { selling_plan: options.selling_plan }),
      }];
      // Page mode does NOT request sections (we're navigating away).
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items }),
        });
      } catch (err) {
        this.log.error('Page-mode add failed', err);
      }
      window.location.assign(url);
      return null;
    }

    const items = options.items || [{
      id: options.id,
      quantity: options.quantity || 1,
      properties: { ...(options.properties || {}) },
      ...(options.selling_plan && { selling_plan: options.selling_plan }),
    }];

    // ─── One-per-cart guard (digital products) ────────────────────────────
    //
    // All products on this store are digital — a customer should only ever
    // own one copy of each variant. Refuse adds for variants already in
    // the cart instead of incrementing the line. Policy: silent no-op +
    // dispatch an 'add-duplicate' event so UI surfaces (upsell buttons,
    // product page CTA) can flip to "already in cart" state reactively.
    const requestedVariantIds = items.map((it) => Number(it.id)).filter((n) => !Number.isNaN(n));
    const allAlreadyInCart =
      requestedVariantIds.length > 0 &&
      requestedVariantIds.every((vid) =>
        this.items.some((existing) => Number(existing.variant_id) === vid),
      );

    if (allAlreadyInCart) {
      this.log.info('Add skipped — variants already in cart (one-per-cart)', { requestedVariantIds });
      // Dispatch as action: 'add' (not 'add-duplicate') so the drawer's
      // auto-open listener still fires — the user clicked Add to Cart and
      // wants to see the cart. `source.duplicate: true` lets toast/UX
      // surfaces filter out the duplicate case if they care.
      this._dispatchUpdate(
        { action: 'add', source: { addMode: mode, duplicate: true, variantIds: requestedVariantIds } },
        null,
        sourceId,
      );
      return null;
    }
    // ────────────────────────────────────────────────────────────────────────

    this.loading = true;
    this.error = null;
    this.removedItem = null;

    try {
      const outcome = await this._postCart('/cart/add.js', { items }, sourceId);
      if (!outcome) {
        // Aborted (LWW). Loading flag stays — the newer call clears it.
        return null;
      }
      // Refresh full cart so we have the canonical state (add returns only
      // the added items, not the entire cart).
      const fullCart = await this._fetchCart();
      if (fullCart) this._merge(fullCart);
      this.loading = false;

      // Source surfaces the add mode so drawers / surfaces can react.
      const source = { addMode: mode, pageUrl: options.pageUrl || null };
      this._dispatchUpdate({ action: 'add', source }, outcome.sectionsForEvent, sourceId);

      this.log.info('Added to cart', { items, item_count: this.item_count });
      return outcome.result;
    } catch (err) {
      this.error = err?.message || err?.description || 'Add to cart failed';
      this.loading = false;
      // Loading state already cleared above on success path. We still emit
      // an update so projections see the cleared loading flag.
      this._dispatchUpdate({ action: 'add-error' }, null, sourceId);
      throw err;
    }
  }

  /**
   * Change a line item's quantity by key.
   *
   * Setting quantity to 0 removes the item server-side.
   */
  async changeItem(options = {}) {
    const key = options.key;
    const sourceId = options.sourceId || null;

    if (key) {
      this.itemLoading = { ...this.itemLoading, [key]: true };
    }
    this.error = null;

    const action = options.quantity === 0 ? 'remove' : 'change';
    this.log.debug('Changing item', { key, quantity: options.quantity });

    // Dispatch a synchronous "loading started" tick so projections immediately
    // reflect itemLoading. Without this, the reactive flag wouldn't reach the
    // store until the response lands.
    this._dispatchUpdate({ action: `${action}-loading` }, null, sourceId);

    try {
      const outcome = await this._postCart(
        '/cart/change.js',
        { id: key, quantity: options.quantity },
        sourceId,
      );

      if (!outcome) {
        // Aborted — leave loading; newer call will clear.
        return null;
      }

      const { result, isLatest, sectionsForEvent } = outcome;
      if (result) this._merge(result);

      if (isLatest) {
        // Latest response — clear all per-item loading flags. Morph will
        // happen via the cart:update listener (the section morph adapter).
        this.itemLoading = {};
      }

      this._dispatchUpdate({ action }, sectionsForEvent, sourceId);
      return result;
    } catch (err) {
      this.error = err?.message || err?.description || 'Update failed';
      if (key) {
        const { [key]: _drop, ...rest } = this.itemLoading;
        this.itemLoading = rest;
      }
      this._dispatchUpdate({ action: `${action}-error` }, null, sourceId);
      throw err;
    }
  }

  /**
   * Remove a line item by key. Snapshots the removed item for undoRemove().
   */
  async removeItem(key) {
    // Snapshot for undo (skip upsell-marked items — they have their own UI).
    const item = this.items.find((i) => i.key === key);
    if (item && !item.properties?._upsell) {
      this.removedItem = {
        id: item.variant_id,
        title: item.product_title,
        image: item.image
          ? (item.image.includes('?')
              ? item.image.replace(/(\.\w+)\?/, '$1?width=80&')
              : item.image + '?width=80')
          : null,
        quantity: item.quantity,
        properties: item.properties || {},
      };
    }
    return this.changeItem({ key, quantity: 0 });
  }

  /**
   * Re-add the most recently removed item. Consumes the snapshot.
   */
  async undoRemove() {
    if (!this.removedItem) return null;
    const { id, quantity, properties } = this.removedItem;
    this.removedItem = null;
    // Re-add with mode 'silent' — the user already saw the removal, so the
    // drawer shouldn't auto-open. Callers that want drawer-open on undo can
    // pass their own mode by calling add() directly.
    return this.add({ id, quantity, properties, mode: 'silent' });
  }

  /**
   * Increment a line item's quantity by 1.
   */
  increment(key) {
    const item = this.items.find((i) => i.key === key);
    if (!item) return null;
    return this.changeItem({ key, quantity: item.quantity + 1 });
  }

  /**
   * Decrement a line item's quantity by 1 (min 1).
   */
  decrement(key) {
    const item = this.items.find((i) => i.key === key);
    if (!item || item.quantity <= 1) return null;
    return this.changeItem({ key, quantity: item.quantity - 1 });
  }

  /**
   * Toggle an upsell variant to an EXACT quantity (0 or 1) via /cart/update.js.
   *
   * Unlike add() (which POSTs /cart/add.js and INCREMENTS the line), this SETS
   * an absolute quantity, so it is idempotent: two overlapping toggles for the
   * same variant converge to the same state instead of stacking to qty 2+.
   * That's the robustness guarantee behind the footer upsell checkboxes — the
   * single-tick UI can neither show nor should allow a quantity above 1.
   *
   * @param {number|string} variantId
   * @param {boolean} on  — true → set qty 1, false → set qty 0 (remove)
   * @returns {Promise<object|null>} the updated cart, or null if short-circuited
   */
  async setUpsell(variantId, on) {
    const vid = Number(variantId);
    if (Number.isNaN(vid)) return null;
    const target = on ? 1 : 0;
    const current = this.quantityOf(vid);

    // ─── Robustness guard (YOUR CONTRIBUTION) ───────────────────────────────
    // Decide whether this toggle should run at all. Two redundant cases to
    // consider, using the values already computed above plus `this._upsellPending`:
    //   1. The cart is already at `target` quantity for this variant → nothing
    //      to do (e.g. a stray click that agrees with current state).
    //   2. A toggle for THIS variant is already in flight (`this._upsellPending`
    //      holds `vid`) → the in-flight write already reflects the latest intent.
    // In either case, `return null` to short-circuit. Otherwise fall through.
    // TODO(you): implement the guard (see the request below the edit).

    this._upsellPending.add(vid);
    this.loading = true;
    this.error = null;
    this._dispatchUpdate({ action: 'upsell-loading' }, null);

    try {
      const outcome = await this._postCart('/cart/update.js', { updates: { [vid]: target } });
      if (!outcome) return null; // aborted (LWW) — a newer cart write clears loading
      if (outcome.result) this._merge(outcome.result);
      this.loading = false;
      this._dispatchUpdate({ action: 'upsell', source: { variantId: vid, on } }, outcome.sectionsForEvent);
      this.log.info('Upsell toggled', { variantId: vid, on, item_count: this.item_count });
      return outcome.result;
    } catch (err) {
      this.error = err?.message || err?.description || 'Upsell update failed';
      this.loading = false;
      this._dispatchUpdate({ action: 'upsell-error' }, null);
      throw err;
    } finally {
      this._upsellPending.delete(vid);
    }
  }

  /**
   * Refresh canonical cart state from /cart.js. No section morph.
   */
  async refresh() {
    this.loading = true;
    this.error = null;
    const cart = await this._fetchCart();
    if (cart) this._merge(cart);
    this.loading = false;
    this.itemLoading = {};
    this._dispatchUpdate({ action: 'refresh' }, null);
    return cart;
  }

  // ─── Discount actions ────────────────────────────────────────────────────

  /**
   * Apply a discount code. Uses latestOnly('discount', ...) for LWW safety.
   */
  async applyDiscount(code) {
    if (!code || typeof code !== 'string') return false;
    code = code.trim().toUpperCase();
    if (!code) return false;

    this.discountLoading = true;
    this.discountError = null;
    this.log.info('Applying discount code', { code });

    const latestOnly = window.Theme?.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.discountLoading = false;
      throw new Error('latestOnly not available');
    }

    try {
      const outcome = await latestOnly('discount', async (signal) => {
        await fetch(`/discount/${encodeURIComponent(code)}`, {
          credentials: 'same-origin',
          redirect: 'follow',
          signal,
        });
        // Trigger a no-op /cart/update.js to fetch the updated cart with
        // section payload. This goes through _postCart for LWW under 'cart'.
        return this._postCart('/cart/update.js', {});
      });

      if (outcome === null) {
        this.log.debug('Discount apply aborted (LWW)');
        return false;
      }

      const { result: postOutcome, isLatest } = outcome;
      if (postOutcome) {
        if (postOutcome.result) this._merge(postOutcome.result);
        // Emit the cart update event so projections see the new state and
        // sections morph. Use the postCart sections payload for morph.
        const sections = postOutcome.sectionsForEvent;
        this._dispatchUpdate({ action: 'discount-apply' }, sections, null);
      }

      const wasApplied = (this.discount_codes || []).some(
        (dc) => dc.code && dc.code.toUpperCase() === code && dc.applicable !== false,
      );

      if (!wasApplied) {
        this.discountError = 'This code cannot be applied to your cart';
        if (DiscountFailedEvent) {
          document.dispatchEvent(new DiscountFailedEvent(code, null, null));
        }
        this.log.warn('Discount code not applicable', { code });
      } else {
        if (DiscountAppliedEvent) {
          document.dispatchEvent(new DiscountAppliedEvent(code, this.getState(), null));
        }
        this.log.info('Discount code applied', { code });
      }
      return wasApplied;
    } catch (err) {
      this.log.error('Failed to apply discount', { code, error: err });
      this.discountError = 'Could not apply discount code';
      if (DiscountFailedEvent) {
        document.dispatchEvent(new DiscountFailedEvent(code, err.message, null));
      }
      return false;
    } finally {
      this.discountLoading = false;
      // Emit a final tick so the projection sees discountLoading=false.
      this._dispatchUpdate({ action: 'discount-loading-cleared' }, null);
    }
  }

  /**
   * Remove a discount code. Mirrors the apply() flow but clears and reapplies
   * remaining codes.
   */
  async removeDiscount(codeToRemove) {
    if (!codeToRemove || typeof codeToRemove !== 'string') return false;
    codeToRemove = codeToRemove.trim().toUpperCase();

    this.discountLoading = true;
    this.discountError = null;
    this.log.info('Removing discount code', { code: codeToRemove });

    const latestOnly = window.Theme?.latestOnly;
    if (typeof latestOnly !== 'function') {
      this.discountLoading = false;
      throw new Error('latestOnly not available');
    }

    try {
      const outcome = await latestOnly('discount', async (signal) => {
        const codesToKeep = (this.discount_codes || [])
          .filter((dc) => dc.applicable !== false && dc.code && dc.code.toUpperCase() !== codeToRemove)
          .map((dc) => dc.code);

        // Step 1: clear all discounts.
        await fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ discount: '' }),
          signal,
        });

        // Step 2: re-apply remaining codes.
        for (const c of codesToKeep) {
          await fetch(`/discount/${encodeURIComponent(c)}`, {
            credentials: 'same-origin',
            redirect: 'follow',
            signal,
          });
        }

        // Step 3: fetch updated cart + sections.
        return this._postCart('/cart/update.js', {});
      });

      if (outcome === null) {
        this.log.debug('Discount remove aborted (LWW)');
        return false;
      }

      const { result: postOutcome } = outcome;
      if (postOutcome) {
        if (postOutcome.result) this._merge(postOutcome.result);
        this._dispatchUpdate({ action: 'discount-remove' }, postOutcome.sectionsForEvent, null);
      }

      this.log.info('Discount removed', { code: codeToRemove });
      return true;
    } catch (err) {
      this.log.error('Failed to remove discount', { code: codeToRemove, error: err });
      this.discountError = 'Could not remove discount code';
      return false;
    } finally {
      this.discountLoading = false;
      this._dispatchUpdate({ action: 'discount-loading-cleared' }, null);
    }
  }

  /**
   * Clear the discount error message.
   */
  clearDiscountError() {
    this.discountError = null;
    this._dispatchUpdate({ action: 'discount-error-cleared' }, null);
  }

  // ─── Helpers — same surface the old store exposed ────────────────────────

  formatMoney(cents) {
    return formatMoney(cents);
  }
}

// Register the element. Singleton — there should only ever be one <cart-engine>
// in the DOM at a time (mounted in theme.liquid).
if (!customElements.get('cart-engine')) {
  customElements.define('cart-engine', CartEngine);
  if (window.Theme?.Logger) {
    window.Theme.Logger.ns('cart-engine').info('Registered');
  }
}
